import { html, nothing } from 'lit';
import { property } from 'lit/decorators.js';
import { Boundary } from '../../visualizations/common/mixins/Boundary.js';
import { Compact } from '../../mixins/Compact.js';
import { StyledFactory } from '../../mixins/Styled.js';
import { Weight } from '../../mixins/Weight.js';
import { OneUxElement } from '../../OneUxElement.js';
import { log } from '../../utils/log.js';
import { ShapeBar } from './fragments/shape/Bar.js';
import { ShapeCircle } from './fragments/shape/Circle.js';
import { roundedProgressPercentage } from './utils.js';
import { style } from './style.js';
import { getLanguage, language } from './lang.js';
import { SlotController } from '../../controllers/SlotController.js';
import { Label } from '../../mixins/Label.js';
const Styled = StyledFactory(style);
const BaseClass = Label(Boundary(Compact(Weight(Styled(OneUxElement)))));
export class OneUxProgressElement extends BaseClass {
  static get elementType() {
    return 'one-ux-progress';
  }
  @property({
    type: String
  })
  public accessor shape!: 'bar' | 'circle';
  @property({
    type: Number
  })
  public accessor value!: number;
  @property({
    type: Number
  })
  public accessor total!: number;
  @property({
    type: Boolean
  })
  public accessor ratio = false;
  #slots: SlotController = new SlotController(this, {
    defaultSlot: true
  });
  guardedRender() {
    if (!this.shape) {
      log.error({
        title: '<one-ux-progress> Missing shape, not rendering.',
        details: this
      });
      return;
    }
    if (!this.total) {
      log.error({
        title: '<one-ux-progress> Missing total, not rendering.',
        details: this
      });
      return;
    }
    if (!Number.isFinite(this.value) || this.value < 0) {
      log.error({
        title: '<one-ux-progress> Missing value, not rendering.',
        details: this
      });
      return;
    }
    const language = getLanguage(this);
    const slots = {
      hasDescription: this.#slots.hasDefaultSlotTextContent() || this.#slots.hasDefaultSlot()
    };
    const valueText = this.ratio ? this.weight === 'high' ? `${this.value} / ${this.total}` : `${this.value}/${this.total}` : `${roundedProgressPercentage(this.value, this.total)}%`;
    const altValueText = !this.ratio ? `${this.value}/${this.total}` : `${roundedProgressPercentage(this.value, this.total)}%`;
    return html`<div
      class="one-ux-element--root"
      role="group"
      aria-roledescription=${this.#getRoleDescription(language)}
      aria-label=${this.label}
    >
      <div
        class="one-ux-accessibility--screen-reader"
        role="progressbar"
        aria-valuenow=${this.value}
        aria-valuemin="0"
        aria-valuemax=${this.total}
        aria-valuetext=${valueText}
      ></div>

      ${this.shape === 'bar' ? ShapeBar({
      compact: this.compact,
      label: this.label,
      shadowRoot: this.shadowRoot!,
      total: this.total,
      value: this.value,
      weight: this.weight,
      slots,
      valueText,
      altValueText
    }) : nothing}
      ${this.shape === 'circle' ? ShapeCircle({
      compact: this.compact,
      label: this.label,
      shadowRoot: this.shadowRoot!,
      total: this.total,
      value: this.value,
      weight: this.weight,
      valueText,
      slots
    }) : nothing}
    </div>`;
  }
  #getRoleDescription = (language: language) => {
    switch (this.shape) {
      case 'bar':
        return language.translations.progressBar;
      case 'circle':
        return language.translations.progressCircle;
    }
    throw new Error('Missing role description');
  };
}